import {useAuth} from "../providers/authprovider";
import {Fragment, useMemo, useState} from "react";
import useUserDataStore from "../hooks/useUserDataStore";
import FinancialGoalCard from "../components/Goals/FinancialGoalCard";
import useGoalStore from "../hooks/useGoalStore";
import {PlusCircleIcon} from "@heroicons/react/16/solid";
import {Layout} from "../components/Layout";
import {NewGoalSidebar} from "../components/Goals/NewGoalSidebar";
import {EditGoalSidebar} from "../components/Goals/EditGoalSidebar";
import {decodeLoggedAt, formatMoney, toDateTime} from "../utils/utils";
import {isAfter, isBefore} from "date-fns";

const navigation = [
    { name: 'Home', href: '/', current: false },
    { name: 'Goals', href: '/goals', current: true },
    // { name: 'Team', href: '#', current: false },
    // { name: 'Projects', href: '#', current: false },
    // { name: 'Calendar', href: '#', current: false },
    // { name: 'Reports', href: '#', current: false },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const stats = [
    { name: 'Invest CZK 50,000 by 2025', stat: '71,897', previousStat: '70,946', change: '12%', changeType: 'increase' },
    { name: 'Reach ~250,000 USD Portfolio Value', stat: '58.16%', previousStat: '56.14%', change: '2.02%', changeType: 'increase' },
    { name: 'Avg. Click Rate', stat: '24.57%', previousStat: '28.62%', change: '4.05%', changeType: 'decrease' },
]

export const Goals = () => {
    const authData = useAuth()
    const {userData} = useUserDataStore()
    const goalStore = useGoalStore(userData?.accountId)
    const [isNewGoalSidebarOpen, setIsNewGoalSidebarOpen] = useState(false)
    const [isEditGoalSidebarOpen, setIsEditGoalSidebarOpen] = useState(false)
    // const updateExpenseSidebarData = useUpdateExpenseSidebar()

    console.log('Goals:goals', goalStore.goals)

    console.log('App auth: ', authData)

    // Helper function to calculate goal data
    const calculateGoalData = (goal) => {
        const normalisedInvestmentPlanEntries = (userData?.investmentPlanEntries || []).map(entry => ({
            ...entry,
            loggedAt: toDateTime(decodeLoggedAt(entry.loggedAt))
        }));

        const filterNormalisedInvestmentPlanEntries = normalisedInvestmentPlanEntries.filter(entry => {
            return isAfter(entry.loggedAt, new Date(goal.createdAt)) && isBefore(entry.loggedAt, new Date(goal.deadlineAt))
        });

        let totalInvestedAmountInCents = filterNormalisedInvestmentPlanEntries.reduce(
            (acc, entry) => {return acc + entry.costInCents}, 0) + (goal.currentAmountSavedInCents || 0);

        totalInvestedAmountInCents = Math.min(totalInvestedAmountInCents, goal.targetAmountInCents);

        return {
            currentAmount: totalInvestedAmountInCents / 100,
            targetAmount: goal.targetAmountInCents / 100,
            title: goal.customTitle || `Invest ${formatMoney(goal.targetAmountInCents/100)} by ${new Date(goal.deadlineAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
            subtitle: goal.customTitle ? `Invest ${formatMoney(goal.targetAmountInCents/100)} by ${new Date(goal.deadlineAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : '',
            createdDate: new Date(goal.createdAt)
        };
    };

    // Calculate goal statistics
    const goalStats = goalStore.goals.reduce((stats, goal) => {
        const goalData = calculateGoalData(goal);
        const progress = Math.min((goalData.currentAmount / goalData.targetAmount) * 100, 100);
        stats.total++;
        if (progress >= 100) {
            stats.completed++;
        }
        return stats;
    }, { total: 0, completed: 0 });

        if (!authData.currentUser) return null

        const user = {
            name: authData.currentUser.displayName,
            email: authData.currentUser.email,
            imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
        }

    return <Layout pageSlug="goals"
    >
        <div className="h-screen mx-auto pt-8 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
            <h1 className="text-3xl ml-1 font-bold text-gray-900 mb-2">🎯 Savings Goals</h1>
            <p className="text-lg text-gray-600 mb-4">Track your progress towards your financial dreams</p>
            
            {/* Goal Statistics */}
            <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                    <span className="text-sm font-medium text-gray-700">
                        {goalStats.completed} completed
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span className="text-sm font-medium text-gray-700">
                        {goalStats.total - goalStats.completed} in progress
                    </span>
                </div>
                <div className="text-sm text-gray-500">
                    {goalStats.total} total goals
                </div>
            </div>
            
            {/* Create New Goal Button */}
            <div className="mb-8">
                <button
                    onClick={() => setIsNewGoalSidebarOpen(true)}
                    className="group relative w-full bg-gradient-to-r from-indigo-100 to-purple-100 hover:from-indigo-200 hover:to-purple-200 text-indigo-700 font-medium py-3 px-4 rounded-xl border border-indigo-200 hover:border-indigo-300 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                    <div className="flex items-center justify-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors duration-200">
                            <PlusCircleIcon className="h-4 w-4 text-indigo-600" />
                        </div>
                        <span className="text-sm">Create New Financial Goal</span>
                    </div>
                </button>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
                {goalStore.goals
                    .map((goal) => {
                        const goalData = calculateGoalData(goal);
                        const progress = Math.min((goalData.currentAmount / goalData.targetAmount) * 100, 100);
                        return { goal, goalData, progress };
                    })
                    .sort((a, b) => {
                        // Sort by completion status: incomplete first, then completed
                        if (a.progress < 100 && b.progress >= 100) return -1;
                        if (a.progress >= 100 && b.progress < 100) return 1;
                        // If both have same completion status, sort by creation date (newest first)
                        return new Date(b.goal.createdAt) - new Date(a.goal.createdAt);
                    })
                    .map(({ goal, goalData }) => (
                        <div 
                            key={goal.id} 
                            className="cursor-pointer"
                            onClick={() => {
                                setIsEditGoalSidebarOpen(true);
                                goalStore.selectGoal(goal);
                            }}
                        >
                            <FinancialGoalCard
                                title={goalData.title}
                                subtitle={goalData.subtitle}
                                currentAmount={goalData.currentAmount}
                                targetAmount={goalData.targetAmount}
                                createdDate={goalData.createdDate}
                                currency="$"
                                showCompleted={true}
                            />
                        </div>
                    ))}
            </div>
        </div>
        <NewGoalSidebar isOpen={isNewGoalSidebarOpen} setIsOpen={setIsNewGoalSidebarOpen} goalStore={goalStore} />
        <EditGoalSidebar isOpen={isEditGoalSidebarOpen} setIsOpen={setIsEditGoalSidebarOpen} goalStore={goalStore} />
    </Layout>
}
